import { Map, Marker, Popup, TileLayer } from "react-leaflet";
import React, { useEffect, useRef, useState } from "react";
import { message, Select } from "antd";
import L from "leaflet"; // For custom marker icon

import marker from "../../../assets/icons/location-fill.svg";
import close from "../../../assets/icons/close-circle.svg";
import "leaflet/dist/leaflet.css"; // Import Leaflet CSS
import "./style.css";

// Fix default marker icon for Webpack
delete L.Icon.Default.prototype._getIconUrl;
const customIcon = L.icon({
  iconUrl: marker,
  iconRetinaUrl: marker,
  shadowUrl: "https://unpkg.com/leaflet@1.3.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], // Size of the icon (width, height)
  iconAnchor: [12, 41], // Point of the icon that corresponds to the marker's location (relative to top-left)
  popupAnchor: [1, -34], // Point where the popup opens (relative to iconAnchor)
  shadowSize: [41, 41], // Size of the shadow
});

export default function AudienceLocationFilter({ setLocationFilter, audienceName, location, setLocation }) {
  const [distanceValue, setDistanceValue] = useState(10); // Default distance
  const [position, setPosition] = useState({ latitude: 51.505, longitude: -0.09 }); // Default to London
  const [searchLocation, setSearchLocation] = useState("");

  const inputRef = useRef(null);
  const mapRef = useRef(null); // Reference to the Map component
  const autocompleteRef = useRef(null);

  // Location Filter
  const selectDistanceOptions = [
    { value: 5, label: "+5 km" },
    { value: 10, label: "+10 km" },
    { value: 15, label: "+15 km" },
    { value: 20, label: "+20 km" },
    { value: 25, label: "+25 km" },
    { value: 50, label: "+50 km" },
    { value: 100, label: "+100 km" },
  ];

  // Helper function to extract city, state, and country from address components
  const extractAddressComponents = (addressComponents) => {
    let city = "";
    let state = "";
    let country = "";

    addressComponents.forEach((component) => {
      if (component.types.includes("locality") || component.types.includes("administrative_area_level_2")) {
        city = component.long_name;
      }
      if (component.types.includes("administrative_area_level_1") || component.types.includes("locality")) {
        state = component.long_name;
      }
      if (component.types.includes("country")) {
        country = component.long_name;
      }
    });

    return { city, state, country };
  };

  useEffect(
    function () {
      setLocationFilter((prevLocationFilter) => ({ ...prevLocationFilter, name: audienceName }));
    },
    [audienceName]
  );

  // Load Google Maps API and initialize autocomplete
  useEffect(() => {
    const loadGoogleMapsScript = () => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyD656YHmwQkieoKwzJopN31fZmr9Vly7w0&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = initializeAutocomplete;
        script.onerror = () => {
          console.error("Failed to load Google Maps API.");
          message.error("Failed to load location search. Please try again later.");
        };
        document.head.appendChild(script);
        return () => document.head.removeChild(script);
      } else {
        initializeAutocomplete();
      }
    };

    const initializeAutocomplete = () => {
      if (!window.google || !window.google.maps || !window.google.maps.places || !inputRef.current) {
        console.error("Google Maps Places API or input element not available.");
        message.error("Failed to initialize location search.");
        return;
      }

      const autoComplete = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ["geocode"],
        fields: ["formatted_address", "geometry", "name", "address_components"],
        componentRestrictions: { country: [] }, // Allow all countries
      });

      autocompleteRef.current = autoComplete;

      const handlePlaceChanged = () => {
        const place = autoComplete.getPlace();
        if (!place.geometry || !place.geometry.location) {
          message.error("Please select a valid location from the suggestions.");
          return;
        }

        const latitude = place.geometry.location.lat();
        const longitude = place.geometry.location.lng();
        const locationName = place.formatted_address || place.name || "";
        const { city, state, country } = place.address_components
          ? extractAddressComponents(place.address_components)
          : { city: "", state: "", country: "" };

        console.log(state);
        setPosition({ latitude, longitude });
        setSearchLocation(locationName);
        setLocation(locationName);

        // Update map center
        if (mapRef.current) {
          mapRef.current.leafletElement.setView([latitude, longitude], 15);
        }

        setLocationFilter((prevState) => ({ ...prevState, city, state, country, latitude, longitude, range: distanceValue }));
      };

      autoComplete.addListener("place_changed", handlePlaceChanged);

      // Cleanup listener on unmount
      return () => {
        if (autocompleteRef.current) {
          window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
        }
      };
    };

    loadGoogleMapsScript();
  }, [setLocationFilter, distanceValue]);

  // Get current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude } = pos.coords;
          setPosition({ latitude, longitude });
          getGoogleLocation(latitude, longitude, true);
        },
        (err) => {
          message.error("Failed to get your location.");
          console.error(err);
        }
      );
    } else {
      message.error("Geolocation is not supported by this browser.");
    }
  }, []);

  // Reverse geocode to get location name
  const getGoogleLocation = async (latitude, longitude, changeValue) => {
    const apiKey = "AIzaSyD656YHmwQkieoKwzJopN31fZmr9Vly7w0";
    const apiUrl = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${latitude},${longitude}&key=${apiKey}`;
    try {
      const response = await fetch(apiUrl);
      const data = await response.json();
      if (data.results.length > 0 && changeValue) {
        const name = data.results[0].formatted_address;

        const { city, state, country } = data.results[0].address_components
          ? extractAddressComponents(data.results[0].address_components)
          : { city: "", state: "", country: "" };

        console.log(data.results);
        console.log(data.results[0]);
        console.log(data.results[0].address_components);
        console.log(state);
        setLocation(name);
        setLocationFilter((prevState) => ({ ...prevState, city, state, country, latitude, longitude }));
      } else {
        message.error("Could not retrieve location name.");
      }
    } catch (error) {
      console.error("Error fetching location:", error);
      message.error("Failed to fetch location details.");
    }
  };

  // Handle map click events
  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current.leafletElement;
      const handleMapClick = (e) => {
        const { lat, lng } = e.latlng;
        setPosition({ latitude: lat, longitude: lng });
        getGoogleLocation(lat, lng, true);
      };

      map.on("click", handleMapClick);

      return () => {
        map.off("click", handleMapClick);
      };
    }
  }, [mapRef.current, setLocationFilter, distanceValue]);

  // Invalidate map size after rendering
  useEffect(() => {
    if (mapRef.current) {
      const map = mapRef.current.leafletElement;
      setTimeout(() => {
        map.invalidateSize();
      }, 0);
    }
  }, []);

  return (
    <div>
      <p style={{ marginBottom: 20 }}>Detected Location</p>
      <div style={{ padding: 20, backgroundColor: "#EEEDFA", borderRadius: 10 }}>
        <div className="location-filter-map-container">
          <Map
            ref={mapRef}
            center={[position.latitude, position.longitude]}
            zoom={15}
            scrollWheelZoom={true}
            zoomControl={false}
            className="location-filter-map"
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            {position.latitude && position.longitude && (
              <Marker position={[position.latitude, position.longitude]} icon={customIcon}>
                <Popup>Dental Clinic</Popup>
              </Marker>
            )}
          </Map>

          <input
            ref={inputRef}
            type="text"
            placeholder="Search location"
            value={searchLocation}
            onChange={(e) => setSearchLocation(e.target.value)}
            onClick={(e) => e.stopPropagation()} // Handle input click to prevent map click
            className="location-filter-map-search"
          />
        </div>

        <div style={{ width: "100%", position: "relative" }}>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Location"
            style={{
              borderRadius: 10,
              border: "1px solid #6B43B5",
              padding: "10px 20px",
              width: "100%",
            }}
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 20,
              position: "absolute",
              right: 0,
              top: "50%",
              transform: "translateY(-50%)",
              paddingRight: 15,
            }}
          >
            <Select
              options={selectDistanceOptions}
              defaultValue={10}
              onChange={(value) => {
                setDistanceValue(value);
                // Update filter when distance changes
                const updatedFilter = {
                  name: location,
                  latitude: position.latitude,
                  longitude: position.longitude,
                  range: value,
                };
                setLocationFilter(updatedFilter);
              }}
              value={distanceValue}
              optionLabelProp="label"
              style={{ backgroundColor: "transparent", width: 100, color: "#848696" }}
              className="select-input-no-border"
            />
            <img
              src={close}
              alt="clear"
              style={{ cursor: "pointer" }}
              onClick={() => {
                setSearchLocation("");
                setLocation("");
                setLocationFilter({
                  name: "",
                  city: "",
                  state: "",
                  country: "",
                  latitude: "",
                  longitude: "",
                  range: 10,
                }); // Reset filter
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
